import { CHECKS, checkTalkLine, readCheck } from '../../../../shared/check-handoff.js';

/**
 * A self-check page's behaviour (pages/CheckPage.tsx, one question at a time
 * since 2026-10-27). The answers are read here, in the browser, by the shared
 * check module (shared/check-handoff.js) — the band, the echoed chips, the
 * finding that leads, and the first line Ember would get — and nothing leaves
 * the page until the person taps Send, whose link carries the answers as digits
 * so /talk writes the same line. Every question, band and finding is already in
 * the server-rendered HTML; this only shows the right ones.
 */
export function mountCheck(root: HTMLElement): () => void {
  const page = root.querySelector<HTMLElement>('[data-check]');
  const slug = page?.dataset.check || '';
  const intro = root.querySelector<HTMLElement>('[data-check-intro]');
  const form = root.querySelector<HTMLElement>('[data-check-form]');
  const result = root.querySelector<HTMLElement>('[data-check-result]');
  const questions = Array.from(root.querySelectorAll<HTMLElement>('[data-q]'));
  if (!page || !intro || !form || !result || !CHECKS[slug] || questions.length !== CHECKS[slug].questions.length) return () => {};

  const bars = Array.from(root.querySelectorAll<HTMLElement>('[data-check-progress] i'));
  const back = root.querySelector<HTMLElement>('[data-check-back]')!;
  const count = root.querySelector<HTMLElement>('[data-check-count]')!;
  const beam = root.querySelector<SVGGElement>('[data-check-beam]')!;
  let answers: (number | null)[] = questions.map(() => null);
  let step = 0;
  let timer = 0;

  const show = (screen: HTMLElement) => {
    for (const s of [intro, form, result]) s.hidden = s !== screen;
    window.scrollTo({ top: 0 });
  };

  const ask = (i: number) => {
    step = i;
    questions.forEach((q, j) => { q.hidden = j !== i; });
    bars.forEach((bar, j) => bar.classList.toggle('on', j <= i));
    back.style.visibility = i === 0 ? 'hidden' : '';
    count.textContent = `${i + 1} of ${questions.length}`;
    questions[i].querySelectorAll<HTMLElement>('[data-v]').forEach((opt) => {
      opt.setAttribute('aria-pressed', String(Number(opt.dataset.v) === answers[i]));
    });
  };

  const finish = () => {
    const digits = answers.join('');
    const reading = readCheck(slug, answers as number[]);
    result.querySelectorAll<HTMLElement>('[data-band]').forEach((el) => { el.hidden = el.dataset.band !== reading.band; });
    const chips = result.querySelector<HTMLElement>('[data-check-chips]')!;
    chips.replaceChildren(...reading.chips.map((text) => Object.assign(document.createElement('span'), { textContent: text })));
    result.querySelector<HTMLElement>('[data-check-told]')!.hidden = reading.chips.length === 0;
    const findings = result.querySelector<HTMLElement>('[data-check-findings]')!;
    const lead = findings.querySelector<HTMLElement>(`[data-finding="${reading.lead}"]`)!;
    findings.querySelectorAll('[data-finding]').forEach((el) => el.classList.toggle('is-lead', el === lead));
    findings.prepend(lead);
    result.querySelector<HTMLElement>('[data-check-line]')!.textContent = checkTalkLine(slug, digits);
    result.querySelector<HTMLAnchorElement>('[data-check-talk]')!.href = `/talk?mode=chat&check=${encodeURIComponent(slug)}&a=${digits}`;
    const tilt = result.querySelector<HTMLElement>(`[data-band="${reading.band}"][data-tilt]`)!.dataset.tilt;
    beam.style.transform = 'rotate(0deg)';
    show(result);
    // The seesaw settles from level onto the answers' tilt once it is on screen.
    requestAnimationFrame(() => requestAnimationFrame(() => { beam.style.transform = `rotate(${tilt}deg)`; }));
  };

  const onClick = (event: Event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-check-start],[data-check-back],[data-check-again],[data-v]');
    if (!target || !root.contains(target)) return;
    window.clearTimeout(timer);
    if (target.matches('[data-check-start]')) {
      show(form);
      ask(0);
    } else if (target.matches('[data-check-back]')) {
      if (step > 0) ask(step - 1);
    } else if (target.matches('[data-check-again]')) {
      answers = questions.map(() => null);
      show(intro);
    } else {
      answers[step] = Number(target.dataset.v);
      ask(step);
      // A beat to see the pick land before the next card.
      timer = window.setTimeout(() => {
        if (step < questions.length - 1) {
          ask(step + 1);
          questions[step].querySelector<HTMLElement>('[data-v]')?.focus({ preventScroll: true });
        } else {
          finish();
        }
      }, 220);
    }
  };

  root.addEventListener('click', onClick);
  return () => {
    window.clearTimeout(timer);
    root.removeEventListener('click', onClick);
  };
}
